import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import axiosInstance from './axiosInstance';
import './styles/GatewayTester.css';
import TokenManager from './TokenManager';
import ProtectedComponent from './ProtectedComponent';
import { AuthContext } from './context/AuthContext';
import { getSocket, initiateSocket } from './socket';

/* ───────────────── helpers ───────────────── */
const sleep = ms => new Promise(r => setTimeout(r, ms));

/* wait until gateway networkStatus === CONNECTED (≤15 min) */
const waitUntilConnected = async ({ mac, company, accessToken,
                                    pollMs = 15_000, maxMs = 900_000 }) => {
  const headers = { Authorization: `Bearer ${accessToken}` };
  const params  = { gateway: mac, company, store: company };
  const start = Date.now();

  while (Date.now() - start < maxMs) {
    try {
      const r = await axios.get(
        'https://eastus.common.solumesl.com/common/api/v2/common/gateway',
        { headers, params }
      );
      if (r.data.gatewayList?.[0]?.networkStatus === 'CONNECTED') {
        return { ok: true, elapsed: Date.now() - start };
      }
    } catch { /* ignore */ }
    await sleep(pollMs);
  }
  return { ok: false, elapsed: Date.now() - start };
};

/* send reboot to Gateway-Launcher */
const reboot = async ip => {
  if (!ip) return;
  try {
    await axios.post(
      'https://172.16.7.3:9015/api/gatewaylauncher/reboot',
      { ipAddresslist: [ip] },
      { headers: { 'Content-Type': 'application/json' } }
    );
  } catch (e) {
    console.error('reboot', e.response?.data || e.message);
  }
};

/* Determine gateway type from firmware version */
const getGatewayType = (fwVersion) => {
  if (!fwVersion) return 'Unknown';
  if (/^N10\./.test(fwVersion)) return 'SaaS';
  if (/^N\d\./.test(fwVersion)) return 'On-Prem';
  return 'Unknown';
};

/* ───────────────── component ───────────────── */
function GatewayTester() {
  const { user } = useContext(AuthContext);
  const testerName = user?.displayName || user?.email || 'Unknown';

  /* ───────── state ───────── */
  const [activeTab, setActiveTab] = useState('test');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('accessToken') || '');
  const [popup, setPopup] = useState('');
  const [regs, setRegs] = useState([]);
  const [filter, setFilter] = useState('');
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyFilter, setHistoryFilter] = useState({ testType: '', status: '' });

  /* ───────── Socket: real-time gateway updates ───────── */
  useEffect(() => {
    initiateSocket();
    const socket = getSocket();
    if (!socket) return;

    const handleGatewayEvent = (data) => {
      console.log('[socket] gateway-events:', data);

      if (data.type === 'gateway_connected' || data.type === 'gateway_disconnected') {
        const gw = data.gateway;
        if (!gw?.mac) return;

        setRows(prev => {
          const normalizedMac = gw.mac.toLowerCase().replace(/[:-]/g, '');
          const idx = prev.findIndex(r =>
            r.mac?.toLowerCase().replace(/[:-]/g, '') === normalizedMac
          );

          if (data.type === 'gateway_disconnected') {
            // Remove disconnected gateway from list
            if (idx >= 0) {
              console.log(`[gateway] Removing disconnected gateway: ${gw.mac}`);
              return prev.filter((_, i) => i !== idx);
            }
            return prev;
          }

          // gateway_connected
          if (idx >= 0) {
            // Update existing
            const updated = [...prev];
            updated[idx] = { ...updated[idx], ...gw, online: true };
            return updated;
          } else {
            // Add new gateway
            console.log(`[gateway] Adding new connected gateway: ${gw.mac}`);
            return [...prev, { ...gw, online: true }];
          }
        });
      }
    };

    socket.on('gateway-events', handleGatewayEvent);
    return () => socket.off('gateway-events', handleGatewayEvent);
  }, []);

  /* ───────── token refresh ───────── */
  const refreshToken = async () => {
    try {
      const { data } = await axios.post(
        'https://eastus.common.solumesl.com/common/api/v2/token/refresh?company=SAGW',
        { refreshToken: localStorage.getItem('refreshToken') },
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );
      const t = data.responseMessage.access_token;
      localStorage.setItem('accessToken', t);
      setToken(t);
      return t;
    } catch (e) {
      console.error('refreshToken', e);
      return null;
    }
  };

  /* ───────── Load history ───────── */
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ page: historyPage, limit: 25 });
      if (historyFilter.testType) params.append('testType', historyFilter.testType);
      if (historyFilter.status) params.append('status', historyFilter.status);

      const { data } = await axiosInstance.get(`/gateway-tester/history?${params}`);
      setHistory(data.tests || []);
      setHistoryTotal(data.pagination?.pages || 1);
    } catch (e) {
      console.error('loadHistory', e);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, historyFilter]);

  useEffect(() => {
    if (activeTab === 'history') loadHistory();
  }, [activeTab, loadHistory]);

  /* ───────── fetchGatewayInfo (for SaaS status) ───────── */
  const fetchGatewayInfo = useCallback(async mac => {
    let registered = false, company = '', status = 'UNASSIGNED', fw = '', net = '';

    try {
      const r = await axios.get(
        'https://eastus.common.solumesl.com/common/api/v2/common/gateway/dps/enrollment',
        { headers: { Authorization: `Bearer ${token}` }, params: { gateway: mac } }
      );
      if (r.status === 200) {
        registered = (r.data.status || '').toLowerCase() === 'assigned';
        company = r.data.company || '';
        status = registered ? 'ASSIGNED' : 'UNASSIGNED';
      }
    } catch (e) {
      if (e.response?.status === 401 && e.response.data?.responseMessage === 'Unauthorized: TOKEN_EXPIRED') {
        const nt = await refreshToken();
        if (nt) return fetchGatewayInfo(mac);
      }
    }

    const tryComp = async comp => {
      try {
        const g = await axios.get(
          'https://eastus.common.solumesl.com/common/api/v2/common/gateway',
          { headers: { Authorization: `Bearer ${token}` }, params: { gateway: mac, company: comp, store: comp } }
        );
        return g.data.gatewayList?.[0];
      } catch { return null; }
    };

    let gRow = await tryComp(company || 'SAGW');
    if (!gRow && company && company !== 'SAGW') gRow = await tryComp('SAGW');

    if (gRow) {
      registered = true;
      status = 'ASSIGNED';
      company = company || gRow.company || '';
      net = gRow.networkStatus || '';
      fw = gRow.version || '';
    }
    if (!registered && regs.includes(mac)) status = 'REGISTERING';
    return { registered, company, status, fwType: fw, net };
  }, [token, regs]);

  /* ───────── writeArticle helper (SaaS) ───────── */
  const writeArticle = async (mac, val) => {
    const body = [{
      articleId: mac,
      articleName: testerName,
      nfcUrl: val,
      eans: [''],
      data: { STORE_CODE: 'SAGW', GATEWAY_MAC: mac, TESTED_BY: testerName, TESTED_ON: new Date().toISOString(), FUNCTIONAL_STATUS: val }
    }];
    try {
      const r = await axios.post(
        'https://eastus.common.solumesl.com/common/api/v2/common/articles',
        body,
        { headers: { Authorization: `Bearer ${token}` }, params: { company: 'SAGW', store: 'SAGW' } }
      );
      return r.status === 200 && r.data.responseMessage === 'SUCCESS';
    } catch (e) {
      console.error('writeArticle', e.response?.data || e.message);
    }
    return false;
  };

  /* ───────── Create/Update test record ───────── */
  const createTestRecord = async (mac, ip, testType, gwType, fwVersion) => {
    try {
      const { data } = await axiosInstance.post('/gateway-tester/test', {
        mac, ip, testType, gwType, firmwareVersion: fwVersion, saasCompany: 'SAGW', saasStore: 'SAGW'
      });
      return data.test;
    } catch (e) {
      console.error('createTestRecord', e);
      return null;
    }
  };

  const updateTestRecord = async (id, status, elapsedSeconds, errorMessage) => {
    try {
      await axiosInstance.patch(`/gateway-tester/test/${id}`, { status, elapsedSeconds, errorMessage });
    } catch (e) {
      console.error('updateTestRecord', e);
    }
  };

  /* ───────── REGISTER (SaaS) ───────── */
  const registerGwSaaS = useCallback(async row => {
    const { mac, ip, fwType } = row;
    const testRecord = await createTestRecord(mac, ip, 'SaaS', 'SaaS', fwType);
    const testId = testRecord?._id;
    const testDate = new Date().toISOString();

    try {
      await axios.post(
        'https://eastus.common.solumesl.com/common/api/v2/common/gateway/dps/enrollment',
        { gatewayMacList: [mac] },
        { headers: { Authorization: `Bearer ${token}` }, params: { company: 'SAGW', store: 'SAGW', deviceType: 'GATEWAY' } }
      );

      reboot(ip);
      await writeArticle(mac, 'Registering');
      setRegs(v => [...new Set([...v, mac])]);
      setRows(v => v.map(r => r.mac === mac ? { ...r, status: 'REGISTERING', lastTestedAt: testDate } : r));
      setPopup('Reboot sent – waiting for CONNECTED...');

      const { ok, elapsed } = await waitUntilConnected({ mac, company: 'SAGW', accessToken: token });

      if (ok) {
        await writeArticle(mac, 'PASSED');
        const info = await fetchGatewayInfo(mac);
        setRows(v => v.map(r => r.mac === mac ? { ...r, ...info, lastTestedAt: testDate, lastTestStatus: 'PASSED' } : r));
        setRegs(v => v.filter(m => m !== mac));
        setPopup(`Gateway ${mac} online in ${(elapsed / 1000).toFixed(0)}s`);
        if (testId) await updateTestRecord(testId, 'PASSED', Math.round(elapsed / 1000));
      } else {
        setRows(v => v.map(r => r.mac === mac ? { ...r, lastTestedAt: testDate, lastTestStatus: 'TIMEOUT' } : r));
        setPopup(`Gateway ${mac} not CONNECTED within 15 min`);
        if (testId) await updateTestRecord(testId, 'TIMEOUT', Math.round(elapsed / 1000), 'Did not connect within 15 minutes');
      }
    } catch (e) {
      console.error('registerGwSaaS', e);
      setRows(v => v.map(r => r.mac === mac ? { ...r, lastTestedAt: testDate, lastTestStatus: 'FAILED' } : r));
      setPopup('Registration failed');
      if (testId) await updateTestRecord(testId, 'FAILED', 0, e.message);
    }
    setTimeout(() => setPopup(''), 7000);
  }, [token, fetchGatewayInfo, testerName]);

  /* ───────── REGISTER (On-Prem) ───────── */
  const registerGwOnPrem = useCallback(async row => {
    const { mac, ip, fwType } = row;
    const testRecord = await createTestRecord(mac, ip, 'On-Prem', 'On-Prem', fwType);
    const testId = testRecord?._id;

    try {
      setRows(v => v.map(r => r.mac === mac ? { ...r, status: 'REGISTERING', lastTestedAt: new Date().toISOString() } : r));
      setRegs(v => [...new Set([...v, mac])]);
      setPopup('Registering to On-Prem server...');

      await axiosInstance.post('/gateway-tester/onprem/register', { mac, ip, siteCode: 'GWTS', companyCode: 'EO89', storeCode: 'GWTS' });
      setPopup('Gateway registered – waiting for connection...');

      const start = Date.now();
      const maxMs = 300_000;  // 5 minutes
      const pollMs = 15_000;  // 15 seconds

      while (Date.now() - start < maxMs) {
        try {
          const { data } = await axiosInstance.post('/gateway-tester/onprem/check-connected', { mac });
          const networkStatus = data.status?.networkStatus || data.status?.status || 'Checking...';

          // Update row with current status from AIMS API
          setRows(v => v.map(r => r.mac === mac ? { ...r, status: networkStatus } : r));
          setPopup(`Gateway ${mac}: ${networkStatus}`);

          if (data.connected || networkStatus === 'CONNECTED') {
            const elapsed = Date.now() - start;
            setRows(v => v.map(r => r.mac === mac ? { ...r, status: 'ASSIGNED', lastTestedAt: new Date().toISOString(), lastTestStatus: 'PASSED' } : r));
            setRegs(v => v.filter(m => m !== mac));
            setPopup(`Gateway ${mac} connected in ${(elapsed / 1000).toFixed(0)}s`);
            if (testId) await updateTestRecord(testId, 'PASSED', Math.round(elapsed / 1000));
            setTimeout(() => setPopup(''), 7000);
            return;
          }
        } catch (e) { console.error('check-connected', e); }
        await sleep(pollMs);
      }

      const elapsed = Date.now() - start;
      setRows(v => v.map(r => r.mac === mac ? { ...r, status: 'TIMEOUT', lastTestStatus: 'TIMEOUT' } : r));
      setRegs(v => v.filter(m => m !== mac));
      setPopup(`Gateway ${mac} not connected within 5 min`);
      if (testId) await updateTestRecord(testId, 'TIMEOUT', Math.round(elapsed / 1000), 'Did not connect within 5 minutes');
    } catch (e) {
      console.error('registerGwOnPrem', e);
      setRows(v => v.map(r => r.mac === mac ? { ...r, lastTestStatus: 'FAILED' } : r));
      setPopup('On-Prem registration failed');
      if (testId) await updateTestRecord(testId, 'FAILED', 0, e.message);
    }
    setTimeout(() => setPopup(''), 7000);
  }, []);

  /* ───────── FACTORY RESET (SaaS) ───────── */
  const factoryResetSaaS = useCallback(async row => {
    const { mac, company } = row;
    const cmp = company || 'SAGW';
    try {
      await axios.delete(
        'https://eastus.common.solumesl.com/common/api/v2/common/gateway/dps/enrollment',
        { headers: { Authorization: `Bearer ${token}` }, data: { gatewayMacList: [mac] }, params: { company: cmp, deviceType: 'GATEWAY' } }
      );
      setPopup(`Factory reset sent for ${mac}`);
      setRows(v => v.map(r => r.mac === mac ? { ...r, status: 'UNASSIGNED', company: '' } : r));
    } catch (e) {
      console.error('factoryResetSaaS', e);
      setPopup('Factory reset failed');
    }
    setTimeout(() => setPopup(''), 6000);
  }, [token]);

  /* ───────── DELETE (On-Prem) ───────── */
  const deleteOnPrem = useCallback(async row => {
    const { mac, gatewayId } = row;
    try {
      setPopup('Deleting from On-Prem server...');
      await axiosInstance.delete('/gateway-tester/onprem/delete', { data: { mac, gatewayId } });
      setPopup(`Gateway ${mac} deleted`);
      setRows(v => v.map(r => r.mac === mac ? { ...r, status: 'UNASSIGNED' } : r));
    } catch (e) {
      console.error('deleteOnPrem', e);
      setPopup('Delete failed');
    }
    setTimeout(() => setPopup(''), 6000);
  }, []);

  /* ───────── Auto-discover gateways (like SSH Manager) ───────── */
  const discoverGateways = useCallback(async (forceRefresh = false) => {
    setBusy(true);
    try {
      const url = forceRefresh ? '/gateway/discover?refresh=true' : '/gateway/discover';
      const { data } = await axiosInstance.get(url);
      const gateways = data.gateways || [];

      // Backend now returns fwVersion and gwType from each gateway's /api/v2/gw/config
      // Just need to check SaaS status for SaaS gateways
      const enriched = await Promise.all(
        gateways.map(async gw => {
          const fwType = gw.fwVersion || '';
          const gwType = gw.gwType || getGatewayType(fwType);

          // Check SaaS status if it's a SaaS gateway
          let saasInfo = { registered: false, company: '', status: 'UNASSIGNED' };
          if (gwType === 'SaaS' && token) {
            saasInfo = await fetchGatewayInfo(gw.mac);
          } else if (gwType === 'On-Prem') {
            // For On-Prem: check if ipServer is set (indicates registered)
            saasInfo.status = gw.ipServer ? 'ASSIGNED' : 'UNASSIGNED';
            saasInfo.company = gw.companyCode || '';
          }

          return {
            ...gw,
            fwType,
            gwType,
            status: saasInfo.status,
            company: saasInfo.company
          };
        })
      );

      // Fetch last tested dates from MongoDB
      if (enriched.length > 0) {
        try {
          const macs = enriched.map(g => g.mac).filter(Boolean);
          const { data: testData } = await axiosInstance.post('/gateway-tester/test/bulk-last-tested', { macs });
          if (testData.lastTested) {
            enriched.forEach(gw => {
              const macUpper = gw.mac?.toUpperCase();
              if (macUpper && testData.lastTested[macUpper]) {
                gw.lastTestedAt = testData.lastTested[macUpper].lastTestedAt;
                gw.lastTestStatus = testData.lastTested[macUpper].status;
              }
            });
          }
        } catch (e) {
          console.error('fetch lastTested', e);
        }
      }

      setRows(enriched);
    } catch (e) {
      console.error('discoverGateways', e);
    } finally {
      setBusy(false);
    }
  }, [token, fetchGatewayInfo]);

  /* ───────── Initial discovery (webhook handles updates via Socket.IO) ───────── */
  useEffect(() => {
    discoverGateways();
  }, [discoverGateways]);

  /* ───────── Action button renderer ───────── */
  const renderActionButton = (row) => {
    const { status, gwType } = row;

    if (status === 'REGISTERING') {
      return <span className="status-testing">Testing...</span>;
    }

    if (gwType === 'SaaS') {
      if (status === 'ASSIGNED') {
        return <button className="btn-factory-reset" onClick={() => factoryResetSaaS(row)}>Factory Reset</button>;
      }
      return <button className="btn-register" onClick={() => registerGwSaaS(row)}>Test</button>;
    } else if (gwType === 'On-Prem') {
      if (status === 'ASSIGNED') {
        return <button className="btn-delete" onClick={() => deleteOnPrem(row)}>Delete</button>;
      }
      return <button className="btn-register" onClick={() => registerGwOnPrem(row)}>Test</button>;
    }

    return <span className="status-unknown">-</span>;
  };

  /* ───────── History Tab ───────── */
  const selectStyle = {
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundColor: '#444',
    color: '#ffffff',
    border: '1px solid #666',
    padding: '10px 35px 10px 14px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    minWidth: '140px',
    cursor: 'pointer',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center'
  };

  const renderHistory = () => (
    <div className="history-tab">
      <div className="history-filters">
        <select
          value={historyFilter.testType}
          onChange={e => setHistoryFilter(f => ({ ...f, testType: e.target.value }))}
          style={selectStyle}
        >
          <option style={{ backgroundColor: '#444', color: 'white' }} value="">All Types</option>
          <option style={{ backgroundColor: '#444', color: 'white' }} value="SaaS">SaaS</option>
          <option style={{ backgroundColor: '#444', color: 'white' }} value="On-Prem">On-Prem</option>
        </select>
        <select
          value={historyFilter.status}
          onChange={e => setHistoryFilter(f => ({ ...f, status: e.target.value }))}
          style={selectStyle}
        >
          <option style={{ backgroundColor: '#444', color: 'white' }} value="">All Status</option>
          <option style={{ backgroundColor: '#444', color: 'white' }} value="PASSED">Passed</option>
          <option style={{ backgroundColor: '#444', color: 'white' }} value="FAILED">Failed</option>
          <option style={{ backgroundColor: '#444', color: 'white' }} value="TIMEOUT">Timeout</option>
        </select>
        <button onClick={loadHistory}>Refresh</button>
      </div>

      {historyLoading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
          <table className="history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>MAC</th>
                <th>Tester</th>
                <th>Type</th>
                <th>Status</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={h._id || i}>
                  <td>{new Date(h.createdAt).toLocaleString()}</td>
                  <td>{h.mac}</td>
                  <td>{h.testerName}</td>
                  <td>{h.testType}</td>
                  <td className={`status-${h.status?.toLowerCase()}`}>{h.status}</td>
                  <td>{h.elapsedSeconds ? `${h.elapsedSeconds}s` : '-'}</td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan="6" className="no-data">No test history</td></tr>}
            </tbody>
          </table>
          <div className="pagination">
            <button onClick={() => setHistoryPage(p => Math.max(1, p - 1))} disabled={historyPage === 1}>Prev</button>
            <span>Page {historyPage} of {historyTotal || 1}</span>
            <button onClick={() => setHistoryPage(p => Math.min(historyTotal, p + 1))} disabled={historyPage >= historyTotal}>Next</button>
          </div>
        </>
      )}
    </div>
  );

  /* ───────── UI ───────── */
  return (
    <ProtectedComponent functionCode="OP03" functionName="Gateway Tester">
      <div className="gateway-tester">
        <TokenManager onTokenUpdate={setToken} />

        <h2>Gateway Tester</h2>

        <div className="toolbar">
          <div className="tabs">
            <button className={activeTab === 'test' ? 'active' : ''} onClick={() => setActiveTab('test')}>Test</button>
            <button className={activeTab === 'history' ? 'active' : ''} onClick={() => setActiveTab('history')}>History</button>
          </div>
          <button className="btn-refresh" onClick={() => discoverGateways(true)} disabled={busy}>
            {busy ? 'Scanning...' : 'Refresh'}
          </button>
          <input
            type="text"
            className="filter-input"
            placeholder="Filter by MAC, IP, Company, Store, Site, Server..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          <span className="tester-name">Tester: <strong>{testerName}</strong></span>
        </div>

        {activeTab === 'test' ? (
          <div className="test-tab">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    {[
                      { key: 'mac', label: 'MAC Address' },
                      { key: 'ip', label: 'IP Address' },
                      { key: 'fwType', label: 'Firmware' },
                      { key: 'gwType', label: 'Type' },
                      { key: 'companyCode', label: 'Company' },
                      { key: 'storeCode', label: 'Store' },
                      { key: 'siteCode', label: 'Site Code' },
                      { key: 'ipServer', label: 'Server' },
                      { key: 'status', label: 'Status' },
                      { key: 'lastTestedAt', label: 'Last Tested' },
                    ].map(col => (
                      <th
                        key={col.key}
                        className="sortable"
                        onClick={() => {
                          if (sortCol === col.key) {
                            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                          } else {
                            setSortCol(col.key);
                            setSortDir('asc');
                          }
                        }}
                      >
                        {col.label} {sortCol === col.key && (sortDir === 'asc' ? '▲' : '▼')}
                      </th>
                    ))}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows
                    .filter(r => {
                      if (!filter) return true;
                      const f = filter.toLowerCase();
                      return (
                        r.mac?.toLowerCase().includes(f) ||
                        r.ip?.toLowerCase().includes(f) ||
                        r.companyCode?.toLowerCase().includes(f) ||
                        r.storeCode?.toLowerCase().includes(f) ||
                        r.siteCode?.toLowerCase().includes(f) ||
                        r.ipServer?.toLowerCase().includes(f) ||
                        r.gwType?.toLowerCase().includes(f) ||
                        r.status?.toLowerCase().includes(f)
                      );
                    })
                    .sort((a, b) => {
                      if (!sortCol) return 0;
                      const aVal = (a[sortCol] || '').toString().toLowerCase();
                      const bVal = (b[sortCol] || '').toString().toLowerCase();
                      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
                      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
                      return 0;
                    })
                    .map((r, i) => (
                    <tr key={r.mac || i}>
                      <td className="mac">{r.mac}</td>
                      <td className="ip">{r.ip}</td>
                      <td>{r.fwType || '-'}</td>
                      <td className={`type-${r.gwType?.toLowerCase()}`}>{r.gwType || '-'}</td>
                      <td>{r.companyCode || '-'}</td>
                      <td>{r.storeCode || '-'}</td>
                      <td>{r.siteCode || '-'}</td>
                      <td>{r.ipServer || '-'}</td>
                      <td className={`status-${r.status?.toLowerCase()}`}>{r.status}</td>
                      <td className={r.lastTestStatus ? `test-${r.lastTestStatus?.toLowerCase()}` : ''}>
                        {r.lastTestedAt
                          ? `${new Date(r.lastTestedAt).toLocaleDateString()} / ${r.lastTestStatus || '?'}`
                          : '-'}
                      </td>
                      <td>{renderActionButton(r)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan="12" className="no-data">{busy ? 'Scanning network...' : 'No gateways found'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : renderHistory()}

        {popup && <div className="popup">{popup}</div>}
      </div>
    </ProtectedComponent>
  );
}

export default GatewayTester;
